import * as tts from "./speech.js";
import { API_BASE } from "./api.js";
import * as liveVoice from "./live-voice.js";
import * as crowd from "./crowd.js";
import { mediaBytes } from "./media.js";
import * as mix from "./mix.js";
import type { Lang } from "./commentary.js";

/**
 * Spoken commentary.
 *
 * Two sources, in order of preference:
 *
 * 1. PRE-RENDERED CLIPS. Complete sentences rendered once with a neural voice
 *    and served as MP3. Each is a single natural utterance, which is what makes
 *    it sound like a person rather than a phone reading text. Roughly 130 clips
 *    across 32 event types, several deep, so four boundaries in an over are
 *    four different calls.
 *
 * 2. THE DEVICE VOICE, for anything a clip cannot cover — the score at the end
 *    of an over, the target, the result. Those contain names and numbers that
 *    change every match and so cannot be pre-rendered.
 *
 * The split is deliberate: the bulk of ball-by-ball gets the good voice, and
 * the announcements fall back rather than being dropped.
 */

export interface Clip { file: string; text: string }
type Manifest = Record<string, Clip[]>;

/**
 * Voice packs.
 *
 * Each is a complete set of recordings — every ball, wicket and situational
 * line. There is no way to mix them: the clips are whole sentences, so a second
 * voice means a second set, which is why there are two per language rather than
 * ten.
 */
export interface Pack { id: string; lang: Lang; label: string; note: string }

export const PACKS: Pack[] = [
  { id: "en",        lang: "en", label: "George", note: "British male · measured" },
  { id: "en-daniel", lang: "en", label: "Daniel", note: "British male · brisk" },
  { id: "hi",        lang: "hi", label: "Omega",  note: "Hindi male" },
  { id: "hi-alpha",  lang: "hi", label: "Alpha",  note: "Hindi female" },
];

export function packsFor(lang: Lang): Pack[] {
  return PACKS.filter((p) => p.lang === lang);
}

/** The chosen pack for a language, remembered between sessions. */
export function getPack(lang: Lang): string {
  try {
    const v = localStorage.getItem(`orbit.pack.${lang}`);
    if (v && PACKS.some((p) => p.id === v && p.lang === lang)) return v;
  } catch { /* private mode */ }
  return lang;
}

export function setPack(lang: Lang, id: string): void {
  try { localStorage.setItem(`orbit.pack.${lang}`, id); } catch { /* private mode */ }
  // Drop the loaded manifest so the next line comes from the new voice.
  if (ctxLang === lang) { manifest = null; ctxPack = ""; }
}



let manifest: Manifest | null = null;
let loading: Promise<Manifest | null> | null = null;
let ctxLang: Lang = "en";

const lastPick: Record<string, number> = {};

const cached: Record<string, Manifest> = {};
let ctxPack = "";

export function load(lang: Lang = "en"): Promise<Manifest | null> {
  const pack = getPack(lang);
  if (cached[pack]) {
    manifest = cached[pack]!; ctxLang = lang; ctxPack = pack;
    return Promise.resolve(manifest);
  }
  if (loading && ctxPack === pack) return loading;
  ctxLang = lang; ctxPack = pack;
  // Through the media store, so a downloaded bank still knows what it contains
  // when there is no network.
  loading = mediaBytes(`/api/v1/commentary/${pack}/manifest.json`)
    .then((b) => (b ? (JSON.parse(new TextDecoder().decode(b)) as Manifest) : null))
    .then((m: Manifest | null) => {
      manifest = m; loading = null;
      if (m) cached[pack] = m;
      return m;
    })
    .catch(() => { loading = null; return null; });
  return loading;
}


/** Which clip bank an event maps to. Mirrors the text engine's keys. */
/**
 * Which clip bank a delivery belongs in, read from the delivery itself.
 *
 * This used to be inferred by matching the commentary prose with regexes, and
 * the prose is written to be varied — so the moment a line did not happen to
 * contain the word "single" or start with "FOUR", the match fell through to
 * the catch-all and the ball played a dot clip. Measured across every outcome,
 * a quarter of all balls played the wrong audio: a three played a dot 57% of
 * the time, a wide played a dot half the time, and a dot ball announced a
 * wicket 9% of the time.
 *
 * Adding commentary lines made it worse, which is the tell — new prose should
 * not be able to break the audio. The delivery is the fact; the sentence is a
 * description of it, and facts are what this should read.
 */
export interface Outcome {
  wide?: boolean;
  noBall?: boolean;
  byes?: number;
  legByes?: number;
  runsOffBat?: number;
  batBoundary?: string | null;
  wicket?: { kind?: string } | null;
}

export function bankOf(d: Outcome): string {
  // A wicket is the loudest thing that can happen; it outranks the runs.
  if (d.wicket) {
    switch (d.wicket.kind) {
      case "BOWLED": return "bowled";
      case "CAUGHT": return "caught";
      case "LBW": return "lbw";
      case "RUN_OUT": return "runOut";
      case "STUMPED": return "stumped";
      case "HIT_WICKET": return "hitWicket";
      default: return "wicket";
    }
  }
  if (d.wide) return "wide";
  if (d.noBall) return "noBall";
  if (d.byes) return "bye";
  if (d.legByes) return "legBye";

  if (d.batBoundary === "SIX") return "six";
  if (d.batBoundary === "FOUR") return "four";

  const runs = d.runsOffBat ?? 0;
  if (runs >= 6) return "six";
  if (runs >= 4) return "four";
  if (runs === 3) return "three";
  if (runs === 2) return "two";
  if (runs === 1) return "one";
  return "dot";
}

export function bankFor(text: string, kind: string): string | null {
  // Situational lines are keyed by their situation, so the clip and the text
  // can never drift apart — both come from the same source.
  if (kind === "situation") {
    const m = manifest;
    if (!m) return null;
    for (const key of Object.keys(m)) {
      if (!key.startsWith("sit_")) continue;
      if (m[key]!.some((c) => c.text === text)) return key;
    }
    return null;
  }
  if (kind === "over") {
    if (/maiden/i.test(text)) return "maiden";
    if (/expensive|hurt|big over/i.test(text)) return "expensiveOver";
    if (/just \d+ from|tight/i.test(text)) return "tightOver";
    return null;            // the score line itself must be spoken, not canned
  }
  if (kind !== "ball") return null;

  if (/^(SIX)/.test(text)) return "six";
  if (/^(FOUR)/.test(text)) return "four";
  if (/BOWLED|Timber|Castled|Cleaned him|Through the gate/i.test(text)) return "bowled";
  if (/CAUGHT|Skied|Taken at|down the throat/i.test(text)) return "caught";
  if (/\bLBW\b|Trapped|Plumb/i.test(text)) return "lbw";
  if (/RUN OUT|Direct hit|mix.?up/i.test(text)) return "runOut";
  if (/STUMPED/i.test(text)) return "stumped";
  if (/HIT WICKET/i.test(text)) return "hitWicket";
  if (/WICKET|breakthrough/i.test(text)) return "wicket";
  if (/^Wide|called wide|Sprayed down leg/i.test(text)) return "wide";
  if (/No ball/i.test(text)) return "noBall";
  if (/\bbyes\b/i.test(text)) return "bye";
  if (/leg bye/i.test(text)) return "legBye";
  if (/three (runs|taken)|for the third/i.test(text)) return "three";
  if (/\btwo\b|couple/i.test(text)) return "two";
  if (/single|one run|for one/i.test(text)) return "one";
  if (/pressure|restless|climbing/i.test(text)) return "dotPressure";
  return "dot";
}

function pickFrom(bank: string): string | null {
  const clips = manifest?.[bank];
  if (!clips?.length) return null;
  let i = Math.floor(Math.random() * clips.length);
  // Never the same clip twice running from one bank.
  if (clips.length > 1 && i === lastPick[bank]) i = (i + 1) % clips.length;
  lastPick[bank] = i;
  return `${API_BASE}/api/v1/commentary/${ctxPack}/${clips[i]!.file}`;
}

/**
 * Playback, on the audio clock.
 *
 * Audio elements cannot be gapless. Even with the next clip preloaded, the
 * handoff at `onended` costs a frame or two, and the container adds its own
 * priming silence on top. Both were audible between every line.
 *
 * Web Audio decodes each clip to a buffer and schedules it at an exact time on
 * the context clock, so the next line starts the sample after the previous one
 * ends. Decoding also strips the container padding, which is the other half of
 * the problem.
 */

/**
 * Pacing.
 *
 * Zero gap was the wrong target. Commentators do not speak in a continuous
 * stream — they call the ball, then leave a beat before the next thought, and a
 * longer one after a wicket so it lands. Running the lines together reads as
 * hurried in exactly the way a machine does.
 *
 * The first line of a burst is immediate, because the ball has just happened
 * and a delay there feels like lag. Everything after it is spaced.
 */
const PAUSE: Record<string, number> = {
  ball: 0.45,        // one delivery to the next
  situation: 1.15,   // a change of subject deserves room
  over: 0.95,        // the score is its own thought
  target: 1.3,       // let the innings settle before the equation
  result: 1.4,       // and let the match end before calling it
};

/** After these, hold longer — the moment needs to breathe. */
/**
 * How long a spoken line is assumed to take before it reports back.
 *
 * Only a placeholder held between claiming the floor and the voice actually
 * finishing; the real end time replaces it. Four seconds is about an over call.
 */
const SPOKEN_ESTIMATE = 4;

const AFTER_BIG = 0.7;
const BIG = /^(SIX|FOUR)|BOWLED|CAUGHT|LBW|RUN OUT|STUMPED|HIT WICKET|WICKET|Timber|Castled/;

let ctx: AudioContext | null = null;
let bus: GainNode | null = null;
let limiter: DynamicsCompressorNode | null = null;
const buffers = new Map<string, AudioBuffer>();
let playHead = 0;

/**
 * When the non-clip voice expects to stop talking, on the audio clock.
 *
 * There were three ways to make a sound here and none knew about the others.
 * Recorded clips chain sample-accurately along playHead, but any line with a
 * number or a name in it — every over call, every target — has no clip, so it
 * went to the synthesised voice and started playing THAT INSTANT, over the top
 * of whatever clip was mid-sentence. Adding between-ball filler made it
 * constant rather than occasional.
 *
 * One clock for every path. A line waits for whatever is already speaking,
 * whichever kind it was.
 */
let speechUntil = 0;

/** When the room next falls silent, by either path. */
function busyUntil(): number {
  return Math.max(playHead, speechUntil);
}          // when the current queue finishes, on the ctx clock
let live = 0;              // sources still scheduled
let lastWasBig = false;    // a boundary or a wicket just went out

function audio(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();

    /**
     * Why the fader could not get loud enough.
     *
     * The clips are fine — measured at about -17 LUFS with true peaks near
     * -2.3 dBFS, which is roughly the mobile speech target. The chain was the
     * problem: a single gain node whose maximum was 1.0. At 100% the fader was
     * doing nothing at all, so the app could only ever be exactly as loud as
     * the recording, played through a phone speaker, outdoors, next to a game.
     *
     * So the fader now reaches well past unity, and a limiter sits after it to
     * make that safe. Turning gain up without one just clips: the peaks square
     * off and speech turns to crackle, which sounds worse than quiet.
     *
     *   fader -> gain (0 .. +11 dB) -> limiter -> makeup -> out
     *
     * The limiter is a DynamicsCompressor set the way a limiter is set — hard
     * knee, high ratio, fast attack to catch a consonant, slow enough release
     * not to pump between words.
     */
    bus = ctx.createGain();
    bus.gain.value = gainFor(mix.get("commentary"));

    limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;    // start holding well below the ceiling
    limiter.knee.value = 0;          // a limiter, not a compressor
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;    // fast enough for a hard consonant
    limiter.release.value = 0.25;    // slow enough not to pump between words

    // Recovers the level the limiter takes off, staying under 0 dBFS so the
    // device's own output stage is never asked to clip.
    const makeup = ctx.createGain();
    // 1.0: the limiter has little left to take off now that the source is
    // properly mastered, so makeup gain would only push the peaks back over.
    makeup.gain.value = 1.0;

    bus.connect(limiter);
    limiter.connect(makeup);
    makeup.connect(ctx.destination);

    // Ramp rather than jump: setting gain.value under a playing source is a
    // discontinuity in the waveform, which is audible as a click.
    mix.onChange((m) => {
      if (!bus || !ctx) return;
      bus.gain.setTargetAtTime(gainFor(m.commentary), ctx.currentTime, 0.015);
    });
  }
  return ctx;
}

/**
 * Fader position to actual gain.
 *
 * Squared, because loudness is perceived roughly logarithmically: a linear
 * fader spends most of its travel in a range that all sounds equally loud and
 * then jumps at the end. Squaring gives usable control down low and still
 * reaches full boost at the top.
 *
 * MAX_BOOST is +3 dB, and it is small on purpose.
 *
 * It used to be +11 dB, which was the wrong tool. The clips were mastered at
 * about -17 LUFS and no amount of runtime gain fixes a quiet master: measured,
 * every setting loud enough to help also drove the peaks past 0 dBFS, and Web
 * Audio hard-clips there. It got louder by distorting, which is why it sounded
 * worse rather than louder.
 *
 * The clips are now mastered to -12 LUFS with a -1 dBTP ceiling, so the level
 * comes from the source where it belongs. Measured through this exact chain,
 * the full fader gives -13.7 LUFS at a -0.7 dBFS peak — louder than a Spotify
 * master, and clean. 1.6 was the first value that clipped.
 */
const MAX_BOOST = 1.4;

export function gainFor(level: number): number {
  // NaN survives Math.min/max, and a NaN on an AudioParam silences the whole
  // graph rather than failing loudly — so it is checked before the clamp.
  if (!Number.isFinite(level)) return 0;
  const l = Math.min(1, Math.max(0, level));
  return l * l * MAX_BOOST;
}
/** Least-recently-used first, so eviction drops what the match is not using. */
function touch(url: string, buf: AudioBuffer): void {
  buffers.delete(url);
  buffers.set(url, buf);
  while (buffers.size > MAX_DECODED) {
    const oldest = buffers.keys().next().value;
    if (oldest === undefined) break;
    buffers.delete(oldest);
  }
}

async function buffer(url: string): Promise<AudioBuffer | null> {
  const hit = buffers.get(url);
  if (hit) { touch(url, hit); return hit; }
  try {
    // Bytes, not a blob URL. media-src allows blob: so an <audio> element can
    // play one, but connect-src does not — so fetching a blob: URL to feed
    // decodeAudioData was blocked by CSP and every clip silently failed.
    const bytes = await mediaBytes(url.replace(API_BASE, ""));
    if (!bytes) return null;
    const buf = await audio().decodeAudioData(bytes.slice(0));
    touch(url, buf);
    return buf;
  } catch {
    return null;
  }
}

/** Schedule a clip to follow whatever is already queued, after its pause. */
async function schedule(url: string, kind: string, text: string): Promise<void> {
  const buf = await buffer(url);
  const c = audio();
  if (!buf || !bus) return;
  if (c.state === "suspended") void c.resume();

  // Nothing is playing, so this is the first line after a ball: say it now.
  // Otherwise leave the beat that belongs before this kind of line.
  // busyUntil, not playHead: a synthesised over call is still someone talking,
  // and scheduling a clip underneath it is the bug this whole clock exists for.
  const floor = busyUntil();
  const idle = floor <= c.currentTime + 0.02;
  const gap = idle ? 0 : (PAUSE[kind] ?? 0.45) + (lastWasBig ? AFTER_BIG : 0);
  lastWasBig = BIG.test(text);

  // A hair of lead time so a clip decoded just in time is not scheduled in the
  // past, which the API silently drops.
  const at = Math.max(floor + gap, c.currentTime + 0.02);
  const src = c.createBufferSource();
  src.buffer = buf;
  src.connect(bus);
  src.start(at);
  playHead = at + buf.duration;
  live += 1;

  // Duck only while the line is actually speaking, so the crowd comes back up
  // into the pause rather than staying flat through it.
  const lead = Math.max(0, (at - c.currentTime) * 1000);
  window.setTimeout(() => crowd.duck(true), lead);
  src.onended = () => {
    live -= 1;
    if (live <= 0) crowd.duck(false);
  };
}

/**
 * Say a line.
 *
 * A clip if the bank has one for this kind of event, otherwise the device
 * voice. Clips queue so one ball finishes before the next starts; a wicket or a
 * result clears the queue, because a stale backlog is worse than a missed line.
 */
export function say(text: string, kind: string, lang: Lang, opts: { interrupt?: boolean; bank?: string | null } = {}): void {
  // Each language has its own bank. Switching language mid-match must not play
  // English clips over Hindi text.
  if (ctxLang !== lang || ctxPack !== getPack(lang)) void load(lang);
  // An explicit bank comes from the delivery and is always right; the text
  // match is a fallback for lines that have no delivery behind them.
  const bank = opts.bank ?? bankFor(text, kind);
  const url = bank ? pickFrom(bank) : null;

  // The crowd reacts to the moment, not to the clip finishing — a cheer three
  // balls late is worse than none.
  const r = crowd.reactionFor(text, kind);
  if (r) crowd.react(r);

  if (opts.interrupt) stop();

  if (url) {
    // If the scorer is well ahead of the audio, start again from now rather
    // than falling further behind. Three lines of backlog is already too much.
    if (busyUntil() - audio().currentTime > 9) { playHead = 0; speechUntil = 0; }
    void schedule(url, kind, text);
    return;
  }

  /**
   * No clip covers this one — it has a name or a number in it.
   *
   * Wait for the room to go quiet first. This used to fire immediately, which
   * is exactly how two lines ended up on top of each other: a scorer taps a
   * ball, the clip starts, the over completes, and the over call talks straight
   * over it.
   *
   * A generous ceiling on the wait, because a scorer who is several balls ahead
   * should not queue an over call behind a minute of backlog — better late
   * than never, but not that late.
   */
  const c = audio();
  const waitMs = Math.min(Math.max(0, (busyUntil() - c.currentTime) * 1000), 12_000);
  // Claim the floor up front so a second spoken line cannot slip in beside it.
  speechUntil = Math.max(busyUntil(), c.currentTime) + waitMs / 1000 + SPOKEN_ESTIMATE;

  window.setTimeout(() => {
    void liveVoice.say(text, lang, getPack(lang)).then((spoken) => {
      if (!spoken) tts.say(text, { lang, interrupt: opts.interrupt ?? false });
    }).finally(() => {
      // Release the floor as soon as it really finished, rather than holding
      // the estimate — the next line should not wait on a guess.
      speechUntil = Math.min(speechUntil, audio().currentTime + 0.05);
    });
  }, waitMs);
}

export function stop(): void {
  liveVoice.stop();
  speechUntil = 0;
  // Closing the bus cancels everything scheduled on it in one move.
  if (bus && ctx) {
    bus.disconnect();
    bus = ctx.createGain();
    // Rebuilding the bus must not silently restore unity gain and undo the
    // scorer's fader.
    bus.gain.value = gainFor(mix.get("commentary"));
    bus.connect(limiter ?? ctx.destination);
  }
  playHead = 0;
  live = 0;
  lastWasBig = false;
  crowd.duck(false);
  tts.stop();
}

/**
 * Unlock audio inside a user gesture.
 *
 * Both paths need it: an <audio> element and speechSynthesis are each blocked
 * until the user has interacted, and both fail silently.
 */
export function unlock(): void {
  tts.unlock();
  // An AudioContext starts suspended until a gesture resumes it, and the
  // failure is silent.
  const c = audio();
  if (c.state === "suspended") void c.resume();
}

/**
 * Warm the whole bank, decoded, before the first ball.
 *
 * This is what the commentary lag actually was. Playback needs an AudioBuffer,
 * and the old prime only pulled the BYTES of five files — so the first time any
 * given clip was picked it still paid an IndexedDB read plus a decodeAudioData
 * before a sound came out. Each bank holds several alternatives and the pick is
 * random, so in practice almost every ball hit a cold clip: tap the keypad,
 * wait, hear it. The scheduler was never the problem; it was being handed a
 * promise that had not resolved yet.
 *
 * Decoding everything up front costs a few hundred milliseconds once, on a
 * screen where the scorer is still setting up, and buys a keypad that speaks
 * the instant it is pressed for the rest of the match.
 *
 * Bounded concurrency because decodeAudioData is not free and a phone doing
 * thirty at once will drop frames on the very screen this is meant to keep
 * smooth.
 */
const WARM_AT_ONCE = 4;

/**
 * How many decoded clips to hold.
 *
 * Decoded audio is not small. A four-second clip at 24 kHz mono is ~46 kB as
 * mp3 and ~370 kB once decodeAudioData has turned it into float samples — so
 * keeping the whole bank resident, which is what the first cut of this did,
 * meant about 110 MB of buffers on a phone that may only have a gigabyte.
 * Fixing a half-second lag is not worth being killed by the OS.
 *
 * Eighty is roughly 30 MB and comfortably covers a match: the banks that fire
 * every ball stay hot, and the rare ones decode on demand from bytes that are
 * already in IndexedDB by then, so the cost is a decode rather than a fetch.
 */
const MAX_DECODED = 80;


let warming: Promise<void> | null = null;

export async function prime(lang: Lang = "en"): Promise<void> {
  if (warming) return warming;
  warming = (async () => {
    const m = await load(lang);
    if (!m) return;
    const pack = getPack(lang);

    // Ball outcomes first — they are the ones a scorer hears within seconds of
    // opening the keypad. Everything else follows while they pick openers.
    const first = ["dot", "one", "two", "three", "four", "six", "ball"];
    const banks = [...first, ...Object.keys(m).filter((k) => !first.includes(k))];

    // Bounded on purpose. Warming all 294 clips meant a 13.8 MB download
    // before the first ball, which on mobile data at a ground is a worse
    // problem than the lag it was fixing.
    const urls: string[] = [];
    outer: for (const bank of banks) {
      // A few from each bank, not all of them: the point is that whichever
      // line the picker chooses is already warm, and three alternatives cover
      // the common case without paying for twenty.
      for (const c of (m[bank] ?? []).slice(0, 3)) {
        urls.push(`/api/v1/commentary/${pack}/${c.file}`);
        if (urls.length >= MAX_DECODED) break outer;
      }
    }

    let at = 0;
    const workers = Array.from({ length: WARM_AT_ONCE }, async () => {
      while (at < urls.length) {
        const url = urls[at++];
        if (url) await buffer(url);
      }
    });
    await Promise.all(workers);
  })().catch(() => { /* a cold clip is a slow line, not a broken one */ })
      .finally(() => { warming = null; });
  return warming;
}
